import "./AssociatedProjectsPanel.css";

import { useEffect, useMemo, useState } from "react";

import { PlusIcon } from "../../../../../assets";
import { SelectData } from "../../../../../models";
import { SearchCollaboratorsResponse } from "../../../../../service/query";
import { ResultData } from "../../../../../service/Shared";
import {
  Button,
  CursorDataGrid,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
  Select,
} from "../../../../../widget";

type AssociatedProject = SearchCollaboratorsResponse["results"][number]["associatedProjects"][number];

interface AssociatedProjectsPanelProps {
  projects: AssociatedProject[];
  associatedProjectGridData: ResultData[];
  isLinkedToOrganisation: boolean;
  displaySource: string;
  onDisplaySourceChange: (value: string) => void;
  onLinkProject?: () => void;
  dataGridColumns: DataGridColumnDefinitionWithCustomCellFormatter<string | null | undefined>[];
  onAssociatedProjectRowsGridChange: DataGridCursorDataLoadEventHandler;
  onKanaHubAssociatedProjectRowsGridChange: DataGridCursorDataLoadEventHandler;
}

export const AssociatedProjectsPanel = ({
  projects,
  associatedProjectGridData: gridData,
  isLinkedToOrganisation,
  displaySource,
  onDisplaySourceChange,
  onLinkProject,
  dataGridColumns,
  onAssociatedProjectRowsGridChange,
  onKanaHubAssociatedProjectRowsGridChange,
}: AssociatedProjectsPanelProps): JSX.Element | null => {
  const displayOptions: SelectData = [
    { key: "crmLinked", value: "CRM linked" },
    { key: "kanaHub", value: "Kana Hub" },
  ];

  const [refreshGrid, setRefreshGrid] = useState(false);
  const kanaHubColumns = useMemo(() => dataGridColumns.filter((column) => column.key !== "remove"), [dataGridColumns]);
  const safeGridData = gridData ?? [];

  useEffect(() => {
    setRefreshGrid((prev) => !prev);
  }, [projects]);

  return (
    <section className="AssociatedProjectsPanel">
      {displaySource === "kanaHub" ? (
        <>
          <div className="AssociatedProjectsHeader">
            <div>
              <h2>Kana Hub projects</h2>
              <p className="AssociatedProjectsDescription">
                This table shows the projects that this developer organisation is involved with in the Kana Earth
                platform.
              </p>
            </div>
            <div className="AssociatedProjectsHeaderActions">
              {isLinkedToOrganisation && (
                <div className="AssociatedProjectsDisplay">
                  <span className="AssociatedProjectsDisplayLabel">Display</span>
                  <Select
                    value={displaySource}
                    onChange={onDisplaySourceChange}
                    data={displayOptions}
                    placeholder="Select"
                    className="AssociatedProjectsDisplaySelect"
                    labelAlignment="left"
                    corners="rounded"
                    size="small"
                    fontSize="body2"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="KanaHubAssociatedProjectsTable">
            <CursorDataGrid
              key="kanaHubAssociatedProjectsGrid"
              columns={kanaHubColumns}
              data={[]}
              clientSide={false}
              onChange={onKanaHubAssociatedProjectRowsGridChange}
              showTotalCount
              pageable
              filterable={false}
              sortable={false}
              variant="seed"
              noDataMessage="No associated projects available."
              refresh={refreshGrid}
            />
          </div>
        </>
      ) : (
        <>
          <div className="AssociatedProjectsHeader">
            <div>
              <h2>CRM linked projects</h2>
              <p className="AssociatedProjectsDescription">
                This table shows the projects your organisation has linked with this company’s CRM record.
              </p>
            </div>
            <div className="AssociatedProjectsHeaderActions">
              {isLinkedToOrganisation && (
                <div className="AssociatedProjectsDisplay">
                  <span className="AssociatedProjectsDisplayLabel">Display</span>
                  <Select
                    value={displaySource}
                    onChange={onDisplaySourceChange}
                    data={displayOptions}
                    placeholder="Select"
                    className="AssociatedProjectsDisplaySelect"
                    labelAlignment="left"
                    corners="rounded"
                    size="small"
                    fontSize="body2"
                  />
                </div>
              )}
              <Button
                className="AssociatedProjectsLinkButton"
                text="Link project"
                icon={<PlusIcon />}
                iconPosition="right"
                variant="primary"
                corners="rounded"
                size="small"
                onClick={onLinkProject}
                disabled={!onLinkProject}
              />
            </div>
          </div>
          <div className="CrmAssociatedProjectsTable">
            <CursorDataGrid
              key="crmAssociatedProjectsGrid"
              columns={dataGridColumns}
              data={safeGridData}
              clientSide
              onChange={onAssociatedProjectRowsGridChange}
              showTotalCount={false}
              sortable={false}
              filterable={false}
              variant="seed"
              noDataMessage="No associated projects available."
              refresh={refreshGrid}
            />
          </div>
        </>
      )}
    </section>
  );
};
